// src/components/founderDashboard/PipelineDashboard.tsx
// The Pipeline tab: challenges being worked up with companies, one
// row per deal. Click a row to edit the details, change the status and add to
// its comment log. Everything is saved through /founder-portal/pipeline/
// (founder_portal/pipeline.py).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "./apiClient";
import { C, FONT, Shell, KpiStrip, Card, Seg, Empty, microLabel, fmt, shortDate, parseDate, th, td, tdNum, TableWrap } from "./hq";

type Comment = { id: number; body: string; author: string; author_id: number | null; created_at: string };
type Deal = {
    id: number; challenge: string; athletes: string; company: string; start_date: string | null;
    value_low: number | null; value_high: number | null; stage: string; status_note: string;
    position: number; created_by: string; created_at: string; updated_at: string; comments: Comment[];
};
type Stage = { key: string; label: string };
type Form = { challenge: string; athletes: string; company: string; start_date: string; value_low: string; value_high: string; stage: string; status_note: string };

const STAGE_COLOR: Record<string, string> = {
    lead: C.neutral, contacted: C.sky, talks: C.gold, proposal: C.rust, won: C.sageD, lost: "rgba(22,25,37,.3)",
};
const CLOSED = new Set(["won", "lost"]);
const SOON_DAYS = 90;

// ---------- money ----------
const money = (n: number) => (n >= 1000 ? `£${fmt(n / 1000, n % 1000 ? 1 : 0)}k` : `£${fmt(n)}`);
const bounds = (d: Deal): [number, number] | null => {
    const lo = d.value_low ?? d.value_high, hi = d.value_high ?? d.value_low;
    return lo === null || hi === null ? null : [lo, hi];
};
const range = (lo: number, hi: number) => (lo === hi ? money(lo) : `${money(lo)} - ${money(hi)}`);
const total = (deals: Deal[]) => {
    let lo = 0, hi = 0, any = false;
    for (const d of deals) {
        const b = bounds(d);
        if (b) { lo += b[0]; hi += b[1]; any = true; }
    }
    return any ? range(lo, hi) : "-";
};

const ago = (iso: string) => {
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    return days <= 0 ? "today" : days === 1 ? "yesterday" : days < 30 ? `${days}d ago` : shortDate(iso);
};
const stamp = (iso: string) => {
    const d = new Date(iso);
    return `${shortDate(iso)}, ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};
const daysUntil = (iso: string | null) => {
    const d = iso ? parseDate(iso) : null;
    if (!d) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return Math.round((d.getTime() - today.getTime()) / 86400000);
};

const toForm = (d?: Partial<Deal>, stage = "lead"): Form => ({
    challenge: d?.challenge || "", athletes: d?.athletes || "", company: d?.company || "", start_date: d?.start_date || "",
    value_low: d?.value_low === null || d?.value_low === undefined ? "" : String(d.value_low),
    value_high: d?.value_high === null || d?.value_high === undefined ? "" : String(d.value_high),
    stage: d?.stage || stage, status_note: d?.status_note || "",
});

const CSS = `
.pl-field{display:flex;flex-direction:column;gap:6px;min-width:0}
.pl-input{font:500 14px ${FONT};color:${C.ink};background:${C.inset};border:1.5px solid transparent;border-radius:10px;padding:10px 12px;outline:none;width:100%;box-sizing:border-box}
.pl-input:focus{border-color:${C.sage};background:#fff}
.pl-btn{font:700 13px ${FONT};border-radius:999px;padding:9px 18px;cursor:pointer;border:1.5px solid ${C.ink};background:#fff;color:${C.ink}}
.pl-btn.primary{background:${C.ink};color:#fff}
.pl-btn:disabled{opacity:.35;cursor:default}
.pl-link{background:none;border:none;padding:0;font:600 12px ${FONT};color:${C.muted};cursor:pointer}
.pl-link:hover{color:${C.ink}}
.pl-drawer{position:fixed;top:0;right:0;bottom:0;width:min(480px,100vw);background:#fff;box-shadow:-12px 0 32px rgba(22,25,37,.14);z-index:40;display:flex;flex-direction:column}
.pl-scrim{position:fixed;inset:0;background:rgba(22,25,37,.25);z-index:39}
`;

// ---------- the side panel ----------
const DealPanel: React.FC<{
    deal: Deal | null; newStage: string; stages: Stage[]; me: number | null;
    onClose: () => void; onSaved: (d: Deal) => void; onDeleted: (id: number) => void;
}> = ({ deal, newStage, stages, me, onClose, onSaved, onDeleted }) => {
    const [form, setForm] = useState<Form>(() => toForm(deal || undefined, newStage));
    const [comment, setComment] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const original = useMemo(() => JSON.stringify(toForm(deal || undefined, newStage)), [deal, newStage]);
    const dirty = JSON.stringify(form) !== original;

    const close = useCallback(() => {
        if ((dirty || comment.trim()) && !window.confirm("Close without saving your changes?")) return;
        onClose();
    }, [dirty, comment, onClose]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [close]);

    const set = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm(f => ({ ...f, [k]: e.target.value }));
    const fail = (e: any) => setErr(e?.response?.data?.detail || "Couldn't save that. Try again.");

    const save = async () => {
        if (!form.challenge.trim()) { setErr("Add the challenge name first."); return; }
        setBusy(true); setErr("");
        try {
            const body = { ...form, start_date: form.start_date || null };
            const r = deal
                ? await api.patch(`/founder-portal/pipeline/${deal.id}/`, body)
                : await api.post("/founder-portal/pipeline/", body);
            onSaved(r.data);
            if (!deal) onClose();
            else setForm(toForm(r.data));
        } catch (e) { fail(e); } finally { setBusy(false); }
    };

    const addComment = async () => {
        if (!deal || !comment.trim()) return;
        setBusy(true); setErr("");
        try {
            const r = await api.post(`/founder-portal/pipeline/${deal.id}/comments/`, { body: comment.trim() });
            setComment(""); onSaved(r.data);
        } catch (e) { fail(e); } finally { setBusy(false); }
    };

    const removeComment = async (id: number) => {
        if (!window.confirm("Remove this comment?")) return;
        try { onSaved((await api.delete(`/founder-portal/pipeline/comments/${id}/`)).data); } catch (e) { fail(e); }
    };

    const remove = async () => {
        if (!deal || !window.confirm(`Delete ${deal.challenge}${deal.company ? ` / ${deal.company}` : ""} and its comments? This can't be undone.`)) return;
        try { await api.delete(`/founder-portal/pipeline/${deal.id}/`); onDeleted(deal.id); } catch (e) { fail(e); }
    };

    const comments = deal ? [...deal.comments].reverse() : [];

    return (<>
        <div className="pl-scrim" onClick={close} />
        <aside className="pl-drawer" role="dialog" aria-label={deal ? `Edit ${deal.challenge}` : "New deal"}>
            <div style={{ padding: "20px 24px 16px", borderBottom: `1px solid ${C.hair}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                    <div style={microLabel}>{deal ? "Deal" : "New deal"}</div>
                    <div style={{ font: `800 22px ${FONT}`, letterSpacing: "-0.01em", marginTop: 4, overflowWrap: "anywhere" }}>{form.challenge || "Untitled"}</div>
                    {deal && <div style={{ font: `400 12px ${FONT}`, color: C.muted, marginTop: 4 }}>Added by {deal.created_by} {ago(deal.created_at)} · updated {ago(deal.updated_at)}</div>}
                </div>
                <button className="pl-link" onClick={close} aria-label="Close" style={{ fontSize: 22, lineHeight: 1 }}>×</button>
            </div>

            <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 12 }}>
                    <label className="pl-field" style={{ gridColumn: "1 / -1" }}><span style={microLabel}>Challenge</span><input className="pl-input" value={form.challenge} onChange={set("challenge")} autoFocus={!deal} /></label>
                    <label className="pl-field"><span style={microLabel}>Athletes</span><input className="pl-input" placeholder="e.g. Jono, Jack" value={form.athletes} onChange={set("athletes")} /></label>
                    <label className="pl-field"><span style={microLabel}>Company</span><input className="pl-input" value={form.company} onChange={set("company")} /></label>
                    <label className="pl-field"><span style={microLabel}>Value from (£)</span><input className="pl-input" inputMode="numeric" placeholder="e.g. 10000" value={form.value_low} onChange={set("value_low")} /></label>
                    <label className="pl-field"><span style={microLabel}>Value to (£)</span><input className="pl-input" inputMode="numeric" placeholder="e.g. 25000" value={form.value_high} onChange={set("value_high")} /></label>
                    <label className="pl-field"><span style={microLabel}>Challenge starts</span><input className="pl-input" type="date" value={form.start_date} onChange={set("start_date")} /></label>
                </div>

                <div className="pl-field">
                    <span style={microLabel}>Status</span>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {stages.map(s => {
                            const on = form.stage === s.key;
                            return (
                                <button key={s.key} type="button" onClick={() => setForm(f => ({ ...f, stage: s.key }))} style={{
                                    display: "flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 999, cursor: "pointer",
                                    font: `${on ? 700 : 600} 12px ${FONT}`, border: `1.5px solid ${on ? C.ink : "rgba(22,25,37,.12)"}`,
                                    background: on ? C.ink : "#fff", color: on ? "#fff" : C.ink,
                                }}>
                                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: STAGE_COLOR[s.key] }} />{s.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <label className="pl-field">
                    <span style={microLabel}>Where it's at</span>
                    <input className="pl-input" maxLength={300} placeholder="e.g. Deck sent, waiting on their marketing lead" value={form.status_note} onChange={set("status_note")} />
                </label>

                {err && <div style={{ font: `500 13px ${FONT}`, color: C.alert }}>{err}</div>}
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <button className="pl-btn primary" disabled={busy || (!!deal && !dirty)} onClick={save}>{deal ? "Save changes" : "Add deal"}</button>
                    {deal && dirty && <button className="pl-btn" disabled={busy} onClick={() => { setForm(toForm(deal)); setErr(""); }}>Undo</button>}
                </div>

                {deal && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 12, borderTop: `1px solid ${C.hair}`, paddingTop: 18, marginTop: 4 }}>
                        <div style={{ ...microLabel, fontSize: 11 }}>Comments · {deal.comments.length}</div>
                        <textarea className="pl-input" rows={3} placeholder="Add an update…" value={comment} onChange={e => setComment(e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); addComment(); } }}
                            style={{ resize: "vertical", lineHeight: 1.45 }} />
                        <div><button className="pl-btn" disabled={busy || !comment.trim()} onClick={addComment}>Add comment</button></div>
                        {comments.map(c => (
                            <div key={c.id} style={{ background: C.inset, borderRadius: 12, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 4 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline" }}>
                                    <span style={{ font: `700 13px ${FONT}` }}>{c.author}</span>
                                    <span style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
                                        <span style={{ font: `400 12px ${FONT}`, color: C.muted }}>{stamp(c.created_at)}</span>
                                        {c.author_id === me && <button className="pl-link" onClick={() => removeComment(c.id)}>Remove</button>}
                                    </span>
                                </div>
                                <div style={{ font: `400 14px/1.45 ${FONT}`, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{c.body}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {deal && (
                <div style={{ padding: "14px 24px", borderTop: `1px solid ${C.hair}` }}>
                    <button className="pl-link" onClick={remove} style={{ color: C.alert }}>Delete deal</button>
                </div>
            )}
        </aside>
    </>);
};

// ---------- the page ----------
type SortKey = "challenge" | "athletes" | "company" | "stage" | "value" | "start_date" | "status_note" | "updated";
const FILTERS = ["Open", "Won", "Lost", "All"];

export default function PipelineDashboard() {
    const [deals, setDeals] = useState<Deal[] | null>(null);
    const [stages, setStages] = useState<Stage[]>([]);
    const [me, setMe] = useState<number | null>(null);
    const [err, setErr] = useState("");
    const [panel, setPanel] = useState<{ id: number | null; stage: string } | null>(null);
    const [filter, setFilter] = useState("Open");
    const [sortKey, setSortKey] = useState<SortKey>("stage");
    const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

    const load = useCallback(() => {
        api.get("/founder-portal/pipeline/")
            .then(r => { setDeals(r.data.deals); setStages(r.data.stages); setMe(r.data.me); setErr(""); })
            .catch(e => setErr(e?.response?.data?.detail || e?.message || "Failed to load"));
    }, []);
    useEffect(load, [load]);

    const stageIndex = useMemo(() => new Map(stages.map((s, i) => [s.key, i])), [stages]);
    const stageLabel = useMemo(() => new Map(stages.map(s => [s.key, s.label])), [stages]);
    const lastTouched = (d: Deal) => {
        const c = d.comments[d.comments.length - 1];
        return c && c.created_at > d.updated_at ? c.created_at : d.updated_at;
    };

    const rows = useMemo(() => {
        const shown = (deals || []).filter(d =>
            filter === "All" ? true : filter === "Won" ? d.stage === "won" : filter === "Lost" ? d.stage === "lost" : !CLOSED.has(d.stage));
        const dir = sortDir === "asc" ? 1 : -1;
        const val = (d: Deal): string | number => {
            switch (sortKey) {
                case "stage": return (stageIndex.get(d.stage) ?? 99) * 1e6 + d.position;
                case "value": { const b = bounds(d); return b ? b[1] : -1; }
                case "start_date": return d.start_date || (dir === 1 ? "9999" : "");
                case "updated": return lastTouched(d);
                default: return (d[sortKey] || "").toLowerCase();
            }
        };
        return [...shown].sort((a, b) => {
            const av = val(a), bv = val(b);
            const primary = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv));
            return primary * dir || a.challenge.localeCompare(b.challenge);
        });
    }, [deals, filter, sortKey, sortDir, stageIndex]);

    const saved = (d: Deal) => setDeals(ds => {
        const list = ds || [];
        return list.some(x => x.id === d.id) ? list.map(x => (x.id === d.id ? d : x)) : [d, ...list];
    });

    const newButton = <button className="pl-btn primary" onClick={() => setPanel({ id: null, stage: "lead" })}>New deal</button>;

    if (err && !deals) return <Shell title="Pipeline" eyebrow="Commercial"><style>{CSS}</style><Empty>{err}</Empty></Shell>;
    if (!deals) return <Shell title="Pipeline" eyebrow="Commercial"><div style={{ color: C.muted, fontFamily: FONT }}>Loading pipeline…</div></Shell>;

    const byStage = (key: string) => deals.filter(d => d.stage === key);
    const open = deals.filter(d => !CLOSED.has(d.stage));
    const won = byStage("won"), lost = byStage("lost"), proposals = byStage("proposal");
    const soon = deals.filter(d => d.stage !== "lost" && (() => { const n = daysUntil(d.start_date); return n !== null && n >= 0 && n <= SOON_DAYS; })())
        .sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
    const panelDeal = panel && panel.id !== null ? deals.find(d => d.id === panel.id) || null : null;

    const head = (label: string, key: SortKey, align: "left" | "right" = "left") => (
        <th onClick={() => {
            if (sortKey === key) setSortDir(d => (d === "asc" ? "desc" : "asc"));
            else { setSortKey(key); setSortDir(key === "value" || key === "updated" ? "desc" : "asc"); }
        }} style={{ ...th(align, sortKey === key), cursor: "pointer", userSelect: "none" }}>
            {label}{sortKey === key ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
        </th>
    );

    return (
        <Shell title="Pipeline" eyebrow="Commercial" right={newButton}>
            <style>{CSS}</style>
            <KpiStrip min={170} size={32} items={[
                { label: "Open deals", value: fmt(open.length), sub: `${fmt(deals.length)} in total` },
                { label: "Open pipeline", value: total(open), sub: "lead to proposal" },
                { label: "Proposals out", value: fmt(proposals.length), sub: proposals.length ? total(proposals) : "none yet" },
                { label: "Won", value: fmt(won.length), sub: won.length ? total(won) : "none yet", color: won.length ? C.sageD : undefined },
                { label: `Starting in ${SOON_DAYS} days`, value: fmt(soon.length), sub: soon[0] ? `next: ${soon[0].challenge}, ${shortDate(soon[0].start_date)}` : "nothing booked" },
                { label: "Lost", value: fmt(lost.length), sub: lost.length ? total(lost) : "-" },
            ]} />

            {err && <div style={{ font: `500 13px ${FONT}`, color: C.alert }}>{err}</div>}

            <Card title="Deals" right={<Seg options={FILTERS} value={filter} onChange={setFilter} mini />}>
                {rows.length ? (
                    <TableWrap minWidth={1100}>
                        <thead>
                            <tr>
                                {head("Challenge", "challenge")}
                                {head("Athletes", "athletes")}
                                {head("Company", "company")}
                                {head("Status", "stage")}
                                {head("Value", "value", "right")}
                                {head("Starts", "start_date", "right")}
                                {head("Where it's at", "status_note")}
                                {head("Last update", "updated", "right")}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(d => {
                                const b = bounds(d);
                                const until = daysUntil(d.start_date);
                                const soonish = until !== null && until >= 0 && until <= SOON_DAYS && !CLOSED.has(d.stage);
                                return (
                                    <tr key={d.id} className="hq-row" tabIndex={0} style={{ cursor: "pointer" }}
                                        onClick={() => setPanel({ id: d.id, stage: d.stage })}
                                        onKeyDown={e => { if (e.key === "Enter") setPanel({ id: d.id, stage: d.stage }); }}>
                                        <td style={{ ...td(), fontWeight: 700, fontSize: 14, whiteSpace: "nowrap", color: d.stage === "lost" ? C.muted : C.ink }}>{d.challenge}</td>
                                        <td style={{ ...td(), color: d.athletes ? "rgba(22,25,37,.75)" : C.faint }}>{d.athletes || "-"}</td>
                                        <td style={{ ...td(), color: "rgba(22,25,37,.75)" }}>{d.company || "-"}</td>
                                        <td style={td()}>
                                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, font: `600 12px ${FONT}`, padding: "4px 10px", borderRadius: 999, background: C.panel, whiteSpace: "nowrap" }}>
                                                <span style={{ width: 8, height: 8, borderRadius: "50%", background: STAGE_COLOR[d.stage] }} />{stageLabel.get(d.stage) || d.stage}
                                            </span>
                                        </td>
                                        <td style={tdNum(600, b ? C.ink : C.faint)}>{b ? range(b[0], b[1]) : "-"}</td>
                                        <td style={{ ...td("right"), whiteSpace: "nowrap" }}>
                                            {d.start_date ? (
                                                <span style={{ fontWeight: soonish ? 700 : 500, color: soonish ? "#9A4A2A" : C.ink }}>
                                                    {shortDate(d.start_date)}{soonish ? <span style={{ fontWeight: 500, color: C.muted }}> · {until}d</span> : null}
                                                </span>
                                            ) : <span style={{ color: C.faint }}>-</span>}
                                        </td>
                                        <td style={{ ...td(), maxWidth: 360, color: d.status_note ? "rgba(22,25,37,.8)" : C.faint }}>{d.status_note || "-"}</td>
                                        <td style={{ ...td("right"), whiteSpace: "nowrap", color: C.muted }}>
                                            {d.comments.length ? `${d.comments.length} comment${d.comments.length === 1 ? "" : "s"} · ` : ""}{ago(lastTouched(d))}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </TableWrap>
                ) : <Empty>{deals.length ? `No ${filter.toLowerCase()} deals.` : "No deals yet. Add the first with New deal."}</Empty>}
            </Card>

            {panel && (
                <DealPanel key={panel.id ?? `new-${panel.stage}`} deal={panelDeal} newStage={panel.stage} stages={stages} me={me}
                    onClose={() => setPanel(null)} onSaved={saved}
                    onDeleted={id => { setDeals(ds => (ds || []).filter(d => d.id !== id)); setPanel(null); }} />
            )}
        </Shell>
    );
}
